import {
  CardGrid,
  DynamicTab,
  DynamicTabs,
  type DynamicTabContentProps,
  Field,
  FieldType,
  FixedToolbarItem,
  FlexToolbarItem,
  Toolbar,
  DynamicTabProps,
  CopyValue,
} from "@bagel-web/components";
import { useQuery } from "@tanstack/react-query";
import Link from "next/link";

import AgentMomentMessages from "./AgentMomentMessages";
import AgentStateCards from "./AgentStateCards";
import { CommonAPI } from "../types/API";
import { agentQueries, eventStreamQueries } from "../queries";
import AgentMomentNavigator from "./AgentMomentNavigator";
import AgentMomentTimeline from "./AgentMomentTimeline";
import AgentMomentFunctions from "./AgentMomentFunctions";
import AgentRecentActionCard from "./AgentRecentActionCard";
import useFilters from "../filter/useFilters";
import useAgentMomentPrefetch, {
  AgentMomentCustomPrefetch,
} from "./useAgentMomentPrefetch";
import { useEffect } from "react";
import useSettledMomentTarget from "./useSettledMomentTarget";
import AgentMomentAPIRequest from "./AgentMomentAPIRequest";
import AgentMomentPlaygroundLink from "./AgentMomentPlaygroundLink";
import AgentHistogram from "../session/histogram/AgentHistogram";
import { Alert } from "react-bootstrap";
import { AgentConfig, CommonAgentMomentList } from "../types";
import SessionLink from "../session/SessionLink";
import AgentHistoryFilterBar from "./AgentHistoryFilterBar";
import { getAgencyTagFilter } from "../tag/AgencyTagFilter";
import AgentViewPreferencesButton from "./viewPreferences/AgentViewPreferencesButton";

export interface AgentHistoryTabContentProps extends DynamicTabContentProps {
  momentId: number;
  sessionId: string;
  agentId: string;
  commonAPI: CommonAPI;
}

export type AgentHistoryTabProps = DynamicTabProps<AgentHistoryTabContentProps>;

export enum AgentHistoryTabs {
  Messages = "messages",
  State = "state",
  APIRequest = "api-request",
  Functions = "functions",
  Playground = "playground",
}

export type AgentHistoryTab = AgentHistoryTabProps | AgentHistoryTabs;

const fields: Array<Field> = [
  {
    name: "momentId",
    label: "Moment ID",
    type: FieldType.Number,
    hidden: true,
  },
  {
    name: "foreignSessionId",
    label: "Foreign Session ID",
    type: FieldType.String,
    hidden: true,
  },
  {
    name: "selectedHistoryTab",
    label: "Selected Tab",
    type: FieldType.String,
    hidden: true,
  },
  {
    name: "tags",
    label: "Tags",
    type: FieldType.String,
    hidden: true,
  },
];

const DEFAULT_TABS: Array<AgentHistoryTabs> = [
  AgentHistoryTabs.State,
  AgentHistoryTabs.Messages,
  AgentHistoryTabs.APIRequest,
  AgentHistoryTabs.Functions,
  AgentHistoryTabs.Playground,
];

const getNearestPreviousMoment = (
  targetMomentId: number | null,
  momentList?: CommonAgentMomentList
): { momentId: number | null; foreignSessionId?: string } | null => {
  if (!momentList) {
    return null;
  }
  const momentIds: Array<number> =
    momentList?.moments.map((m) => m.moment_id) || [];
  momentIds.sort((a, b) => a - b);
  let index = 0;
  while (
    index < momentIds.length &&
    (momentIds[index] as number) < (targetMomentId as number)
  ) {
    index++;
  }
  return momentIds[index - 1]
    ? {
        momentId: momentIds[index - 1] as number,
        foreignSessionId: momentList.moments[index - 1]?.session_id,
      }
    : null;
};

export type AgentHistoryMoment = {
  momentId: number | null;
  foreignSessionId?: string;
};

const AgentHistory = ({
  organization,
  sessionId,
  parentSessionId,
  originMomentId,
  agentId,
  commonAPI,
  customPrefetch,
  tabs = DEFAULT_TABS,
  agentConfig,
}: {
  organization: string;
  sessionId: string;
  parentSessionId?: string | null;
  originMomentId?: number | null;
  agentId: string;
  commonAPI: CommonAPI;
  customPrefetch?: AgentMomentCustomPrefetch;
  tabs?: Array<AgentHistoryTab>;
  agentConfig?: AgentConfig;
}) => {
  const { filterCriteria, handleFilterChange, handleSetFilters } =
    useFilters(fields);

  const setMomentId = (moment: AgentHistoryMoment | null) => {
    if (moment == null || moment?.momentId === null) {
      handleSetFilters({
        ...filterCriteria,
        momentId: { value: "" },
        foreignSessionId: { value: "" },
      });
    } else {
      handleSetFilters({
        ...filterCriteria,
        momentId: { value: moment.momentId.toString() },
        foreignSessionId: {
          value:
            moment.foreignSessionId !== sessionId
              ? moment.foreignSessionId || ""
              : "",
        },
      });
    }
  };

  const setSelectedTab = (selectedTab: string) => {
    handleFilterChange("selectedHistoryTab", selectedTab);
  };

  const momentId: number | null = filterCriteria.momentId?.value
    ? parseInt(filterCriteria.momentId?.value)
    : null;

  // The ID of the session that owns the moment we are currently viewing
  const activeSessionId: string =
    filterCriteria.foreignSessionId?.value || sessionId;

  // The debounced pair the data queries fetch; an isolated selection applies
  // immediately, only rapid navigation is debounced. See
  // useSettledMomentTarget.
  const fetchTarget = useSettledMomentTarget(momentId, activeSessionId);
  const settledMomentId = fetchTarget.momentId;
  const settledSessionId = fetchTarget.sessionId;

  const {
    data: momentList,
    isPending: momentListPending,
    refetch: refetchMomentList,
  } = useQuery({
    queryKey: agentQueries.getMomentList(sessionId, agentId),
    queryFn: () => commonAPI.fetchAgentMomentList({ sessionId, agentId }),
  });

  const { prefetchMomentWindow } = useAgentMomentPrefetch({
    agentId,
    momentId: settledMomentId,
    momentList,
    commonAPI,
    customPrefetch,
  });
  useEffect(() => {
    prefetchMomentWindow();
  }, [settledMomentId, momentList]);

  useEffect(() => {
    if (momentListPending || momentList === undefined) {
      // If the momentList is still loading, do not update the momentId we intend to be active
      return;
    }

    if (
      momentId !== null &&
      !momentList?.moments.some((m) => m.moment_id === momentId)
    ) {
      // If momentId is set (i.e., via URL) but not present in the momentList, reset to the nearest previous moment ID
      setMomentId(getNearestPreviousMoment(momentId, momentList));
    } else if (momentId !== null && activeSessionId === sessionId) {
      // Ensure that the correct foreignSessionId is set if applicable
      const currentMoment = momentList?.moments.find(
        (m) => m.moment_id === momentId
      );
      if (currentMoment?.session_id !== sessionId) {
        setMomentId({
          momentId: momentId,
          foreignSessionId: currentMoment?.session_id,
        });
      }
    } else if (momentId === null && momentList?.moments.length) {
      // If no momentId is set, set it to the latest moment
      const latestMoment = momentList.moments[momentList.moments.length - 1];
      setMomentId(
        latestMoment
          ? {
              momentId: latestMoment.moment_id,
              foreignSessionId: latestMoment.session_id,
            }
          : null
      );
    }
  }, [momentList, momentId]);

  const { data: eventStreamDetail } = useQuery({
    queryKey: eventStreamQueries.getDetail(
      settledSessionId,
      agentId,
      settledMomentId
    ),
    queryFn: () =>
      settledMomentId
        ? commonAPI.fetchEventStreamDetail(
            settledSessionId,
            agentId,
            settledMomentId
          )
        : null,
    staleTime: Infinity,
  });

  const agentCreation = eventStreamDetail?.event_wrappers.find(
    (eventWrapper) => eventWrapper.event_type === "created_agent"
  );

  const requestId =
    eventStreamDetail?.api_request_id || agentCreation?.request_id;

  if (momentList?.moments.length === 0) {
    return (
      <Alert variant="warning">
        <span>No moments found.</span>
        {parentSessionId && (
          <div>
            <Link
              href={`/sessions/${parentSessionId}/agent/${agentId}${originMomentId ? `?momentId=${originMomentId}` : ""}`}
            >
              <i className="me-2 bi-diagram-3-fill" />
              <span>View Agent in Parent Session</span>
            </Link>
          </div>
        )}
      </Alert>
    );
  }

  const agentHasMoment =
    momentList &&
    momentList.moments.findIndex((moment) => moment.moment_id === momentId) !==
      -1;

  const agencyTagFilter = getAgencyTagFilter(
    filterCriteria.tags?.value?.split(",") || []
  );

  return (
    <>
      <AgentHistogram
        sessionId={sessionId}
        agentId={agentId}
        commonAPI={commonAPI}
      />
      <AgentMomentTimeline
        sessionId={sessionId}
        momentId={momentId}
        momentList={momentList}
        onSelectMoment={setMomentId}
      />

      <Toolbar style={{ alignItems: "flex-start" }}>
        <FlexToolbarItem>
          <CardGrid maxColumns={1} minWidth="400px">
            <AgentRecentActionCard
              sessionId={settledSessionId}
              agentId={agentId}
              momentId={settledMomentId}
              commonAPI={commonAPI}
            />
          </CardGrid>
        </FlexToolbarItem>
        <FixedToolbarItem>
          <AgentViewPreferencesButton />
        </FixedToolbarItem>
        <FixedToolbarItem>
          <AgentMomentNavigator
            momentId={momentId}
            momentList={momentList}
            momentListPending={momentListPending}
            setMomentId={setMomentId}
            refetchMomentList={refetchMomentList}
          />
        </FixedToolbarItem>
      </Toolbar>

      <AgentHistoryFilterBar
        organization={organization}
        sessionId={sessionId}
        commonAPI={commonAPI}
        agencyTagFilter={agencyTagFilter}
        onChange={(newTagFilter) => {
          handleSetFilters({
            ...filterCriteria,
            tags: { value: newTagFilter.tags.join(",") },
          });
        }}
      />

      {momentId !== null && activeSessionId !== sessionId && (
        <Alert variant="info">
          <span className="d-flex gap-2 align-items-center">
            {`Moment ${momentId} is from Ancestor Session `}
            <i className="bi-diagram-3-fill" />
            <CopyValue value={activeSessionId}>
              <SessionLink sessionId={activeSessionId} commonAPI={commonAPI} />
            </CopyValue>
          </span>
        </Alert>
      )}

      {settledMomentId !== null && agentHasMoment && (
        <DynamicTabs
          className="mb-4"
          onSelectTab={setSelectedTab}
          selectedTab={filterCriteria.selectedHistoryTab?.value}
        >
          {tabs.map((tab) => {
            if (typeof tab === "string") {
              switch (tab) {
                case AgentHistoryTabs.Messages:
                  return (
                    <DynamicTab
                      key="messages"
                      eventKey="messages"
                      iconClass="bi-chat-right-text-fill"
                      title="Messages"
                      mountOnEnter={true}
                      unmountOnExit={true}
                    >
                      <AgentMomentMessages
                        momentId={settledMomentId}
                        sessionId={settledSessionId}
                        agentId={agentId}
                        commonAPI={commonAPI}
                        agencyTagFilter={agencyTagFilter}
                      />
                    </DynamicTab>
                  );
                case AgentHistoryTabs.State:
                  return (
                    <DynamicTab
                      key="state"
                      eventKey="state"
                      title="Agent State"
                      iconClass="bi-person-lines-fill"
                      mountOnEnter={true}
                      unmountOnExit={true}
                    >
                      <CardGrid maxColumns={2} unitWidth={1750}>
                        <AgentStateCards
                          momentId={settledMomentId}
                          sessionId={settledSessionId}
                          agentId={agentId}
                          commonAPI={commonAPI}
                          agentConfig={agentConfig}
                          agencyTagFilter={agencyTagFilter}
                        />
                      </CardGrid>
                    </DynamicTab>
                  );
                case AgentHistoryTabs.APIRequest:
                  return (
                    <DynamicTab
                      key="api-request"
                      eventKey="api-request"
                      title="API Request"
                      iconClass="bi-arrow-left-right"
                      content={({ onStateChange }) => (
                        <AgentMomentAPIRequest
                          requestId={requestId}
                          organization={organization}
                          commonAPI={commonAPI}
                          setRequestHasError={(hasError) =>
                            onStateChange({ hasError })
                          }
                          setRequestIsLoading={(isLoading) =>
                            onStateChange({ isLoading })
                          }
                        />
                      )}
                    />
                  );
                case AgentHistoryTabs.Functions:
                  return (
                    <DynamicTab
                      key="functions"
                      eventKey="functions"
                      title="Agent Functions"
                      iconClass="bi-braces"
                      mountOnEnter={true}
                      unmountOnExit={true}
                      content={() => (
                        <AgentMomentFunctions
                          momentId={settledMomentId}
                          sessionId={settledSessionId}
                          agentId={agentId}
                          commonAPI={commonAPI}
                          agencyTagFilter={agencyTagFilter}
                        />
                      )}
                    />
                  );
                case AgentHistoryTabs.Playground:
                  return (
                    <DynamicTab
                      key="playground"
                      eventKey="playground"
                      iconClass="bi-play-fill"
                      title="Playground"
                      mountOnEnter={true}
                      unmountOnExit={true}
                      content={() => (
                        <AgentMomentPlaygroundLink
                          sessionId={settledSessionId}
                          agentId={agentId}
                          momentId={settledMomentId}
                        />
                      )}
                    />
                  );
              }
            } else {
              return (
                <DynamicTab
                  key={tab.eventKey}
                  eventKey={tab.eventKey}
                  title={tab.title}
                  iconClass={tab.iconClass}
                  mountOnEnter={tab.mountOnEnter}
                  unmountOnExit={tab.unmountOnExit}
                  content={
                    tab.content
                      ? ({ onStateChange }) => {
                          return tab.content({
                            momentId: settledMomentId,
                            sessionId: settledSessionId,
                            agentId,
                            commonAPI,
                            onStateChange,
                          });
                        }
                      : undefined
                  }
                >
                  {tab.content && tab.children}
                </DynamicTab>
              );
            }
          })}
        </DynamicTabs>
      )}
      <Toolbar $justifyContent="flex-end" className="mt-3">
        <FixedToolbarItem>
          <AgentMomentNavigator
            isPrimary={false}
            momentId={momentId}
            momentList={momentList}
            momentListPending={momentListPending}
            setMomentId={setMomentId}
            refetchMomentList={refetchMomentList}
          />
        </FixedToolbarItem>
      </Toolbar>
    </>
  );
};

export default AgentHistory;
